import { defineStore } from 'pinia'

import {
  addToSection as rtcAddToSection,
  createSection as rtcCreateSection,
  deleteSection as rtcDeleteSection,
  removeFromSection as rtcRemoveFromSection,
  renameSection as rtcRenameSection,
  reorderInSection as rtcReorderInSection,
  reorderSection as rtcReorderSection,
  setConversationFavorite,
} from '~/features-shared/rtconv/api/rtinterface-senders'
import type { RTCSectionThread } from '~/features-shared/rtconv/interfaces/rtconversation'
import {
  getSections,
  addThreadInSection as swymAddThreadInSection,
  createSection as swymCreateSection,
  deleteSection as swymDeleteSection,
  removeFromSection as swymRemoveFromSection,
  renameSection as swymRenameSection,
  reorderSection as swymReorderSection,
} from '~/features-shared/sections/api/sections'

import type {
  Section,
  SectionId,
  SectionSource,
  SectionThread,
} from '~/features-generic/left-panel/interfaces/left-panel'

/**
 * Section store
 */
export const useSectionsStore = defineStore('sections', () => {
  // region state
  const sections = reactive<Map<SectionSource, Map<SectionId, Section<SectionThread>>>>(new Map())
  const FAVORITE_SECTION_ID = '0'
  const expandCollapseState = reactive<Map<string, boolean>>(new Map())

  // region method
  /**
   * Get initial position of thread in particular section
   * @param type SectionSource
   * @param sectionId  section id
   * @returns position of thread in particular section
   */
  function getInitialPosition(type: SectionSource, sectionId: SectionId): number {
    const currentSection = sections?.get(type)
      .get(sectionId)?.threads
    if (currentSection) {
      return currentSection.length + 1
    }
    else {
      return 1
    }
  }

  // region actions
  /**
   * Create section
   * @param config
   * @param config.type SectionSource
   * @param config.name name of section
   * @returns Promise<void>
   */
  const createSection = async ({ type, name }: {
    type: SectionSource
    name: string
  }): Promise<void> => {
    const initialSectionPosition = [...(sections.get(type) || [])]
    if (type === 'externaldm') {
      await rtcCreateSection({ name, position: initialSectionPosition.length + 1 })
    }
    else {
      const result
       = await swymCreateSection({
         name,
         category: type === 'dm' ? 'conversation' : 'community',
         position: initialSectionPosition.length + 1,
       })
      sections.get(type)
        ?.set(result.id, result)
    }
  }

  /**
   * Delete section
   * @param config
   * @param config.type SectionSource
   * @param config.sectionId section id
   * @returns Delete section of give section id
   */
  async function deleteSection({ type, sectionId }: {
    type: SectionSource
    sectionId: SectionId
  }): Promise<void> {
    if (type === 'externaldm') {
      await rtcDeleteSection({ sectionId: Number(sectionId) })
    }
    else if (type === 'dm' || type === 'community') {
      await swymDeleteSection(sectionId)
    }
    sections.get(type)
      ?.delete(sectionId)
    expandCollapseState.delete(sectionId)
  }

  /**
   * Rename section
   * @param config
   * @param config.type SectionSource
   * @param config.sectionName section name
   * @param config.id  section id
   */
  const renameSection = async ({ type, sectionName, id }:
  {
    type: SectionSource
    sectionName: string
    id: SectionId
  }): Promise<void> => {
    if (type === 'externaldm') {
      rtcRenameSection({ name: sectionName, id: Number(id) })
    }
    else if (type === 'dm' || type === 'community') {
      await swymRenameSection({ sectionId: id, name: sectionName })
    }
    const sectionsInSource = sections.get(type)
    const currSection = sectionsInSource?.get(id)
    if (currSection) {
      sectionsInSource?.set(id, {
        ...currSection,
        name: sectionName,
        id,
      })
    }
  }

  /**
   * Remove thread from section
   * @param config
   * @param config.type SectionSource
   * @param config.threadId  thread id
   * @param config.sectionId  section id
   */
  async function removeThreadFromSection({ type, threadId, sectionId }: {
    type: SectionSource
    threadId: string
    sectionId: SectionId
  }): Promise<void> {
    const sectionsInSource = sections.get(type)
    if (type === 'externaldm') {
      rtcRemoveFromSection({
        convId: Number(threadId),
        sectionId: Number(sectionId),
      })
    }
    else if (type === 'dm' || type === 'community') {
      await swymRemoveFromSection({ sectionId, subjectUri: threadId })
    }

    const currSection = sectionsInSource
      ?.get(sectionId)
    const threads = currSection?.threads.filter(thread => thread.threadId !== threadId)
    if (threads && currSection) {
      sectionsInSource
        ?.set(sectionId, {
          ...currSection,
          threads,
        })
    }
  }

  /**
   * Add thread in section
   * @param config
   * @param config.type SectionSource
   * @param config.currSectionId current section id
   * @param config.threadId  thread id
   * @param config.sectionId  section id
   * @param config.position position of section
   */
  async function addThreadInSection({ type, currSectionId, threadId, sectionId, position }: {
    type: SectionSource
    currSectionId: string
    threadId: string
    sectionId: SectionId
    position: number
  }): Promise<void> {
    const sectionsInSource = sections.get(type)
    if (type === 'externaldm') {
      rtcAddToSection({
        convId: Number(threadId),
        sectionId: Number(sectionId),
        position,
      })
    }
    else if (type === 'community' || type === 'dm') {
      await swymAddThreadInSection({ sectionId, subjectUri: threadId, position })
    }

    const oldSection = sectionsInSource?.get(currSectionId)

    if (oldSection) {
      const threads = oldSection?.threads.filter(thread => thread.threadId !== threadId) || []
      sectionsInSource
        ?.set(currSectionId, {
          ...oldSection,
          threads,
        })
    }

    sectionsInSource?.get(sectionId)?.threads.push({ threadId, position })
  }

  /**
   * Reorder section
   * @param config
   * @param config.type SectionSource
   * @param config.sectionId  section id
   * @param config.position position of section
   */
  async function reorderSection({ type, sectionId, position }: {
    type: SectionSource
    sectionId: SectionId
    position: number
  }): Promise<void> {
    if (type === 'externaldm') {
      rtcReorderSection({
        sectionId: Number(sectionId),
        position,
      })
    }
    else if (type === 'community' || type === 'dm') {
      await swymReorderSection({ sectionId, position })
      const category = type === 'dm' ? 'conversation' : 'community'
      const result = await getSections(category)
      const commSection = sections.get(type)
      result.forEach(item => commSection?.set(item.id, item))
    }
  }

  /**
   * Set the thread as favorite.
   * @param config
   * @param config.type SectionSource
   * @param config.currSectionId current section id
   * @param config.threadId  thread id
   * @param config.position position of section
   */
  function setFavorite({ type, currSectionId, threadId, position }: {
    type: SectionSource
    currSectionId: SectionId
    threadId: string
    position: number
  }): void {
    const sectionsInSource = sections.get(type)
    if (type === 'externaldm') {
      setConversationFavorite({
        convId: Number(threadId.split(':')
          .pop() || threadId),
        favOrder: position,
      })
    }

    const oldSection = sectionsInSource?.get(currSectionId)

    if (oldSection) {
      const threads = oldSection?.threads.filter(thread => Number(thread.threadId) !== Number(threadId.split(':')
        .pop() || threadId)) || []
      sectionsInSource?.set(currSectionId, {
        ...oldSection,
        threads,
      })
    }
    sectionsInSource?.get(FAVORITE_SECTION_ID)?.threads.push({ threadId: threadId.split(':')
      .pop() || threadId, position })
  }

  /**
   * Remove from favorites
   * @param config
   * @param config.type SectionSource
   * @param config.threadId  thread id
   */
  function removeFavorite({ type, threadId }: {
    type: SectionSource
    threadId: string
  }): void {
    const sectionsInSource = sections.get(type)
    if (type === 'externaldm') {
      setConversationFavorite({
        convId: Number(threadId.split(':')
          .pop() || threadId),
        favOrder: -1,
      })
    }
    const currSection = sectionsInSource?.get(FAVORITE_SECTION_ID)
    const threads = currSection?.threads.filter(thread => thread.threadId !== threadId.split(':')
      .pop()) || []

    if (currSection) {
      sectionsInSource
        ?.set(FAVORITE_SECTION_ID, {
          ...currSection,
          threads,
        })
    }
  }

  /**
   * Reorder Thread in Section
   * @param config
   * @param config.type  SectionSource
   * @param config.sectionId section id
   * @param config.threadId thread id
   * @param config.position position of thread
   *
   */
  async function reorderThreadIdInSection({ type, threadId, sectionId, position }: {
    type: SectionSource
    sectionId: string
    threadId: string
    position: number
  }): Promise<void> {
    if (type === 'externaldm') {
      const curSectionsInSource = sections.get(type)
      let reorderedThreads: RTCSectionThread[]
      if (sectionId === '0') {
        reorderedThreads = await setConversationFavorite({
          convId: Number(threadId.split(':')
            .pop() || threadId),
          favOrder: position,
          waitForResponse: true,
        })
      }
      else {
        reorderedThreads = await rtcReorderInSection({
          convId: Number(threadId),
          sectionId: Number(sectionId),
          position,
          waitForResponse: true,
        })
      }
      const currSection = curSectionsInSource
        ?.get(sectionId)
      if (currSection) {
        sections.get(type)
          ?.set(sectionId, {
            ...currSection,
            threads: reorderedThreads.map((conv) => {
              return {
                threadId: conv.convId.toString(),
                position: conv.position,
              }
            }),
          })
      }
    }
    else if (type === 'dm' || type === 'community') {
      const result = await swymAddThreadInSection({ sectionId, subjectUri: threadId, position })
      const commSection = sections.get(type)
      result.forEach(item => commSection?.set(item.id, item))
    }
  }

  return {
    // Refs
    sections,
    // Methods
    getInitialPosition,
    // Actions
    createSection,
    deleteSection,
    renameSection,
    addThreadInSection,
    removeThreadFromSection,
    setFavorite,
    removeFavorite,
    reorderSection,
    reorderThreadIdInSection,
    expandCollapseState,
  }
})
